import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Tooltip as AntDToolTip, Button, Card, Popconfirm, Row, Space, Table } from "antd";
import { FormWizard } from "components/formWizard";
import { PageHeader } from "components/pageHeader";
import {
  useCreateDeveloperMutation,
  useDeleteDeveloperMutation,
  useFetchDevelopersQuery,
  useUpdateDeveloperMutation,
} from "features/developers/developersAPI";
import BaseStep from "features/developers/wizardSteps/baseStep";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useRef, useState } from "react";
import styled from "styled-components";
import { getColumnSearchProps } from "common/antd-utils";

const StyledCard = styled(Card)`
  margin-top: 16px;
`;

const StyledRow = styled(Row)`
  margin-bottom: 8px;
`;

const StyledHeader = styled(PageHeader)`
  margin-bottom: 0;
`;

function Developers() {
  const [updateDeveloper] = useUpdateDeveloperMutation();
  const [createDeveloper] = useCreateDeveloperMutation();
  const [currentDeveloper, setCurrentDeveloper] = useState();
  const [isDeveloperWizardView, setIsDeveloperWizardView] = useState(false);
  const [deleteDeveloper] = useDeleteDeveloperMutation();
  const {
    data: {
      [PERMISSIONS.DEVELOPER__GET]: isDeveloperGetAuthorized,
      [PERMISSIONS.DEVELOPER__CREATE]: isDeveloperCreateAuthorized,
      [PERMISSIONS.DEVELOPER__UPDATE]: isDeveloperUpdateAuthorized,
      [PERMISSIONS.DEVELOPER__DELETE]: isDeveloperDeleteAuthorized,
    },
  } = useAuthorizations();
  const { data: developers = [], isLoading } = useFetchDevelopersQuery(
    {},
    {
      skip: !isDeveloperGetAuthorized,
    },
  );

  const onNext = async (developer) => {
    const action = currentDeveloper
      ? updateDeveloper({ ...developer, id: currentDeveloper?.id })
      : createDeveloper(developer);

    const result = await action;

    if ("data" in result) {
      setCurrentDeveloper(result.data);
    }

    return result;
  };

  const handleEditDeveloperClick = (record) => {
    setIsDeveloperWizardView(true);
    setCurrentDeveloper(record);
  };

  const [filteredInfo, setFilteredInfo] = useState({});
  const searchInput = useRef(null);
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };
  const handleReset = (clearFilters) => {
    clearFilters();
  };
  const handleChange = (_, filters) => {
    setFilteredInfo(filters);
  };

  const developerTableColumns = [
    {
      title: "Developer Name",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps("name", searchInput, handleSearch, handleReset),
      // @ts-ignore
      filteredValue: filteredInfo?.name ?? null,
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Creditors",
      dataIndex: "creditors",
      key: "creditors",
      render: (creditors) =>
        creditors?.length > 0 ? creditors.map((creditor) => creditor.name).join(", ") : "-",
    },
    {
      title: "Actions",
      fixed: "right",
      dataIndex: "action",
      key: "action",
      hidden: !isDeveloperUpdateAuthorized && !isDeveloperDeleteAuthorized,
      render: (_, record) => (
        <Space size="middle">
          {isDeveloperUpdateAuthorized && (
            <AntDToolTip placement="bottom" title="Edit" key="edit">
              <EditOutlined key="edit" onClick={() => handleEditDeveloperClick(record)} />
            </AntDToolTip>
          )}
          {isDeveloperDeleteAuthorized && (
            <Popconfirm
              placement="topLeft"
              okText="Yes"
              title="Are you sure you want to delete this developer?"
              onConfirm={() => deleteDeveloper(record)}
            >
              <DeleteOutlined key="delete" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ].filter((column) => !column.hidden);

  const developerSteps = [
    {
      title: "Developer Base info",
      form: <BaseStep initialCreditors={currentDeveloper?.creditors} />,
      onNext: async (data) => onNext(data),
    },
  ];

  const onCancelDeveloperWizard = () => {
    setIsDeveloperWizardView(false);
  };

  const addDeveloper = () => {
    setIsDeveloperWizardView(true);
    setCurrentDeveloper(null);
  };

  return isDeveloperGetAuthorized ? (
    <>
      <StyledHeader>Developers</StyledHeader>
      <StyledRow align="middle">
        <span>Configure developers and relationships.</span>
        {!isDeveloperWizardView && isDeveloperCreateAuthorized ? (
          <Button onClick={addDeveloper} icon={<PlusOutlined />} type="link">
            Add Developer
          </Button>
        ) : null}
      </StyledRow>
      {isDeveloperWizardView ? (
        <StyledCard>
          <FormWizard
            canSkipSteps={!!currentDeveloper}
            steps={developerSteps}
            onCancel={onCancelDeveloperWizard}
            onDone={() => setIsDeveloperWizardView(false)}
            initialValues={
              currentDeveloper
                ? {
                    ...currentDeveloper,
                    creditorIds: currentDeveloper.creditors?.map((creditor) => creditor.id),
                  }
                : {}
            }
          />
        </StyledCard>
      ) : (
        <Table
          loading={isLoading}
          dataSource={developers}
          // @ts-ignore
          columns={developerTableColumns}
          bordered
          scroll={{ x: "max-content" }}
          onChange={handleChange}
        />
      )}
    </>
  ) : null;
}

export default Developers;
